/**
 * Temperature validation utilities
 */

export interface TemperatureValidationResult {
  isValid: boolean;
  error?: string;
  severity?: 'danger' | 'warning' | 'ok';
}

/**
 * Validate temperature is numeric and in reasonable range
 * -50°C to 50°C covers most food storage scenarios
 */
export function validateTemperature(temp: string | number): TemperatureValidationResult {
  const tempNum = typeof temp === 'string' ? parseFloat(temp) : temp;

  if (isNaN(tempNum)) {
    return { isValid: false, error: 'La température doit être un nombre' };
  }

  if (tempNum < -50) {
    return { isValid: false, error: 'La température ne peut pas être inférieure à -50°C' };
  }

  if (tempNum > 50) {
    return { isValid: false, error: 'La température ne peut pas dépasser 50°C' };
  }

  return { isValid: true };
}

/**
 * Check if temperature is within equipment range.
 * A null bound means that side is unconstrained (infinite).
 */
export function checkTemperatureRange(
  temperatureC: number,
  minTempC: number | null,
  maxTempC: number | null
): TemperatureValidationResult {
  if (minTempC != null && temperatureC < minTempC) {
    return {
      isValid: true,
      severity: 'danger',
      error: `Température ${minTempC - temperatureC}°C en dessous du minimum (${minTempC}°C)`,
    };
  }

  if (maxTempC != null && temperatureC > maxTempC) {
    return {
      isValid: true,
      severity: 'danger',
      error: `Température ${temperatureC - maxTempC}°C au-dessus du maximum (${maxTempC}°C)`,
    };
  }

  return {
    isValid: true,
    severity: 'ok',
  };
}

/**
 * Get temperature status badge class
 */
export function getTemperatureStatusClass(
  temperatureC: number,
  minTempC: number | null,
  maxTempC: number | null
): string {
  const check = checkTemperatureRange(temperatureC, minTempC, maxTempC);
  
  switch (check.severity) {
    case 'danger':
      return 'bg-error/10 text-error border-error';
    case 'warning':
      return 'bg-warning/10 text-warning border-warning';
    case 'ok':
    default:
      return 'bg-success/10 text-success border-success';
  }
}

/**
 * Format temperature with symbol. A null/undefined value represents
 * an unconstrained (infinite) bound.
 */
export function formatTemperature(tempC: number | null | undefined): string {
  if (tempC === null || tempC === undefined) return '∞';
  return `${tempC.toFixed(1)}°C`;
}
